import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, RefreshCw, ExternalLink, Eye, Star } from 'lucide-react';
import { listDemoLinks, pitchViewUrl, type DemoLinkRow } from '@/api/salesPitch';

/**
 * DEMOS CHECKED — shops whose owners ran the demo on themselves.
 *
 * A landing page's "watch the demo" button asks the visitor for their own
 * business, saves it, and shows them the same pitch PK makes on a call. Every
 * row here is somebody who typed their own shop's name into our website and
 * watched what their reviews could be. That is not a pitch and it is not a
 * lead — nobody has spoken to them — but it is the warmest signal this site
 * produces without a form, so it gets its own screen rather than being mixed
 * into the pitches PK actually made.
 *
 * Nothing here is editable. It is a list of people worth calling, and the
 * calling happens in Target Leads.
 */

const fmt = (n?: number | null) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));

const when = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

export default function DemosChecked() {
  const [rows, setRows] = useState<DemoLinkRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { items, total: n } = await listDemoLinks('self', 1, 100);
      setRows(items);
      setTotal(n);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.businessName, r.marketCategory, r.area].filter(Boolean).join(' ').toLowerCase().includes(needle)
    );
  }, [rows, q]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Demos checked</h1>
          <p className="text-sm text-muted-foreground">
            Shops whose owners ran the demo on themselves, from a landing page. Nobody has spoken to
            them yet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a shop, trade or area"
              className="w-64 pl-8"
            />
          </div>
          <Button variant="outline" size="icon" onClick={() => void load()} title="Reload">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card className="p-0">
        {error ? (
          <div className="p-6 text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? 'Nobody has run the demo on their own shop yet.'
              : 'No shop here matches that.'}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Trade</TableHead>
                <TableHead className="text-right">Reviews today</TableHead>
                <TableHead className="text-right">Shown as</TableHead>
                <TableHead className="text-right">Opened</TableHead>
                <TableHead>Checked</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.slug}>
                  <TableCell>
                    <div className="font-medium leading-tight">{r.businessName}</div>
                    {r.area ? (
                      <div className="text-xs text-muted-foreground">{r.area}</div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{r.marketCategory || '—'}</span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="inline-flex items-center gap-1">
                      {fmt(r.reviews)}
                      {r.rating != null ? (
                        <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          {r.rating}
                        </span>
                      ) : null}
                    </span>
                  </TableCell>
                  {/* What the pitch told them they could reach. Stored with the
                      row, so this is the number they actually saw. */}
                  <TableCell className="text-right tabular-nums font-medium text-emerald-700">
                    {fmt(r.targetReviews)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="inline-flex items-center gap-1 text-muted-foreground">
                      <Eye className="h-3.5 w-3.5" />
                      {r.views ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{when(r.createdAt)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="icon" asChild title="Open what they saw">
                      <a href={pitchViewUrl(r.slug, r)} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {rows.length ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{total}</Badge>
          <span>
            {total === 1 ? 'shop has' : 'shops have'} checked their own reviews
            {shown.length !== rows.length ? ` · ${shown.length} shown` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
}
